import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { m } from 'framer-motion';
import { Controller } from 'react-hook-form';
import Iconify from 'src/components/iconify';
import useBenefitsDrawerFilters, {
  type BenefitsDrawerApplyPayload,
  type BenefitsDrawerFilterDraft,
} from './benefits-drawer-filters.hook';

interface BenefitsDrawerFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (payload: BenefitsDrawerApplyPayload) => void;
  appliedFilters: BenefitsDrawerFilterDraft | null;
}

export default function BenefitsDrawerFilters(props: BenefitsDrawerFiltersProps) {
  const { isOpen, onClose, onApplyFilters, appliedFilters } = props;
  const hook = useBenefitsDrawerFilters({
    isOpen,
    onClose,
    onApplyFilters,
    appliedFilters,
  });

  const isAllTierSelected = hook.draftFilters.tierSort.includes('all');
  const isCompanyType = hook.draftFilters.localType === 'company';
  const locationFieldOptions = isCompanyType ? hook.companyOptions : hook.locationOptions;
  const locationFieldLabel = isCompanyType ? 'Empresa' : 'Localização';
  const isLocationFieldLoading = isCompanyType ? hook.loadingCompanies : hook.loadingLocations;
  const filterCardSx = {
    borderRadius: 2.5,
    boxShadow: 'none',
    borderColor: 'grey.200',
  };

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 560, md: 620 },
          maxWidth: '100vw',
          backgroundColor: 'grey.50',
        },
      }}
      open={isOpen}
      onClose={onClose}
      anchor="right"
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* <FilterAltOutlinedIcon color="primary" /> */}
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Filtros de Benefícios
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Refine a listagem com os critérios desejados
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>Eventos do Viva Visitante</Typography>
                  <Autocomplete
                    options={hook.businessUnitOptions}
                    loading={hook.loadingBusinessUnits}
                    value={hook.businessUnitOptions.find((option) => option.value === hook.draftFilters.businessUnitId) || null}
                    onChange={(_, option) => hook.handleChangeBusinessUnit(option?.value || '')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText="Nenhuma unidade encontrada"
                    loadingText="Carregando unidades..."
                    renderInput={(params) => <TextField {...params} variant="filled" label="Unidade de negócio" />}
                  />
                  <Autocomplete
                    options={hook.lpEventOptions}
                    loading={hook.loadingLpEvents}
                    value={hook.lpEventOptions.find((option) => option.value === hook.draftFilters.lpEventId) || null}
                    onChange={(_, option) => hook.handleChangeLpEvent(option?.value || '')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText="Nenhum evento encontrado"
                    loadingText="Carregando eventos..."
                    renderInput={(params) => <TextField {...params} variant="filled" label="Evento do Viva Visitante" />}
                  />
                </Stack>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Tipo de benefício
                  </Typography>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.benefitTypeOptions}
                    value={hook.benefitTypeOptions.filter((option) =>
                      hook.draftFilters.benefitType.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeBenefitType(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Tipo de benefício" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Novidade?
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    aria-label="Filtrar benefícios por novidade"
                    value={hook.draftFilters.isNewBenefit}
                    onChange={(_, value: boolean | null) => hook.handleChangeIsNewBenefit(value)}
                    sx={{
                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: 'grey.300',
                        justifyContent: 'flex-start',
                        gap: 1,
                        py: 1.25,
                        px: 1.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        overflow: 'hidden',
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'primary.main' },
                        },
                      },
                    }}
                  >
                    {[
                      { label: 'Sim', value: true, icon: 'mdi:star-four-points-outline' },
                      { label: 'Não', value: false, icon: 'mdi:minus-circle-outline' },
                    ].map((option) => {
                      const selected = hook.draftFilters.isNewBenefit === option.value;

                      return (
                        <ToggleButton key={option.label} value={option.value}>
                          <Box
                            component={m.span}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                            animate={
                              selected ? { scale: [1, 1.08, 1], x: [0, 2, 0] } : { scale: 1, x: 0 }
                            }
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                          >
                            <Iconify icon={option.icon} sx={{ width: 18, height: 18 }} />
                            {option.label}
                          </Box>
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Temas e categorias
                  </Typography>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    loading={hook.loadingCategories}
                    disabled={hook.loadingCategories}
                    options={hook.categoryOptions}
                    value={hook.categoryOptions.filter((option) =>
                      hook.draftFilters.categoriesId.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeCategories(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText="Nenhuma categoria encontrada"
                    loadingText="Carregando categorias..."
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label="Categorias"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {hook.loadingCategories ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    loading={hook.loadingThemes}
                    disabled={hook.loadingThemes}
                    options={hook.themeOptions}
                    value={hook.themeOptions.filter((option) =>
                      hook.draftFilters.themesId.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeThemes(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText="Nenhum tema encontrado"
                    loadingText="Carregando temas..."
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label="Temas"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {hook.loadingThemes ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Typography variant="caption" color="text.secondary">
                    Estes filtros são aplicados somente a produtos e cupons.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Período
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Controller
                      name="startDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de início"
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            hook.handleChangeDate('startDate', value);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'filled',
                            },
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="endDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de fim"
                          value={field.value}
                          minDate={hook.draftFilters.startDate || undefined}
                          onChange={(value) => {
                            field.onChange(value);
                            hook.handleChangeDate('endDate', value);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'filled',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Tier
                  </Typography>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.tierOptions}
                    value={hook.tierOptions.filter((option) =>
                      hook.draftFilters.tierSort.includes(option.value)
                    )}
                    getOptionDisabled={(option) => isAllTierSelected && option.value !== 'all'}
                    onChange={(_, selected) =>
                      hook.handleChangeTierSort(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Tier" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Ambiente de Exibição
                  </Typography>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={hook.exhibitionEnvOptions}
                    value={hook.exhibitionEnvOptions.filter((option) =>
                      hook.draftFilters.exhibitionEnv.includes(option.value)
                    )}
                    onChange={(_, selected) =>
                      hook.handleChangeExhibitionEnv(selected.map((item) => item.value))
                    }
                    getOptionLabel={(option) => option.label}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Ambiente de Exibição" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Empresa ou local
                  </Typography>

                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Tipo de local
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={hook.draftFilters.localType}
                      onChange={(_, value) => {
                        if (!value) {
                          return;
                        }

                        hook.handleChangeLocalType(value);
                      }}
                    >
                      {hook.localTypeOptions.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                          {option.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Stack>

                  <Autocomplete
                    freeSolo={isCompanyType}
                    loading={isLocationFieldLoading}
                    options={locationFieldOptions.map((option) => option.value)}
                    value={hook.draftFilters.company}
                    onChange={(_, value) => hook.handleChangeCompany(value || '')}
                    onInputChange={(_, value) => hook.handleChangeCompany(value)}
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label={locationFieldLabel} />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <Divider />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={hook.handleClear}>
            Limpar
          </Button>
          <Button variant="contained" onClick={hook.handleApply}>
            Aplicar filtros
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
